// src/components/MobileHeader.jsx
import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Divider,
  Box,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import HomeIcon from "@mui/icons-material/Home";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SchoolIcon from "@mui/icons-material/School";
import BuildIcon from "@mui/icons-material/Build";
import InfoIcon from "@mui/icons-material/Info";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { BrandLogo } from '../branding/BrandAsset';
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { selectAuth, logout } from "../store/slices/authSlice";
import { showSuccess } from "../store/slices/alertSlice";

const MobileHeader = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 990);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector(selectAuth);

  // Check screen size and update state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 990);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close drawer when screen size changes to desktop
  useEffect(() => {
    if (!isMobile) {
      setDrawerOpen(false);
    }
  }, [isMobile]);

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleSubmenuToggle = (index) => {
    setOpenSubmenu((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleLogout = async () => {
    await dispatch(logout());
    dispatch(showSuccess('Logged out successfully'));
    setDrawerOpen(false);
    navigate('/');
  };

  const handleNavigation = (path) => {
    setDrawerOpen(false);
    navigate(path);
  };

  // Menu items with icons
  const menuItems = [
    { 
      text: "Home", 
      link: "/", 
      icon: <HomeIcon sx={{ color: '#000080' }} /> 
    },
    {
      text: "Forex",
      icon: <ShowChartIcon sx={{ color: '#000080' }} />,
      submenu: [
        { text: "Forex Signals", link: "/forex-signal" },
        { text: "Forex Brokers", link: "/forex-brokers" },
      ],
    },
    {
      text: "Crypto",
      icon: <AttachMoneyIcon sx={{ color: '#000080' }} />,
      submenu: [
        { text: "Crypto Signals", link: "/crypto-signal" },
        { text: "Crypto Trading Mastery", link: "/crypto-trading-mastery" },
      ],
    },
    {
      text: "Courses",
      icon: <SchoolIcon sx={{ color: '#000080' }} />,
      submenu: [
        { text: "Beginner to Advanced", link: "/courses/beginner" },
        { text: "Personal Strategies", link: "/courses/personal" },
        { text: "Mentorship", link: "/courses/mentorship" },
      ],
    },
    { 
      text: "Services", 
      link: "/services", 
      icon: <BuildIcon sx={{ color: '#000080' }} /> 
    },
    { 
      text: "About", 
      link: "/about", 
      icon: <InfoIcon sx={{ color: '#000080' }} /> 
    },
    { 
      text: "Contact", 
      link: "/contact", 
      icon: <ContactMailIcon sx={{ color: '#000080' }} /> 
    },
    { 
      text: "Ebooks", 
      link: "/ebooks", 
      icon: <MenuBookIcon sx={{ color: '#000080' }} /> 
    },
  ];

  const renderMenuItems = (items) => {
    return items.map((item, index) => {
      const hasSubmenu = item.submenu && item.submenu.length > 0;
      const isOpen = openSubmenu[index];

      return (
        <React.Fragment key={index}>
          <ListItem
            button
            onClick={() => {
              if (hasSubmenu) {
                handleSubmenuToggle(index);
              } else if (item.link) {
                handleNavigation(item.link);
              }
            }}
            sx={{
              py: 1.5,
              px: 2,
              borderRadius: '8px',
              mx: 1,
              mb: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0,0,128,0.05)',
              },
            }}
          >
            {item.icon && (
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText 
              primary={item.text}
              primaryTypographyProps={{
                style: { 
                  fontWeight: 500,
                  fontSize: '15px',
                  color: '#1a1a1a',
                },
              }}
            />
            {hasSubmenu ? (
              isOpen ? <ExpandLess sx={{ color: '#000080' }} /> : <ExpandMore sx={{ color: '#000080' }} />
            ) : null}
          </ListItem>
          
          {hasSubmenu && (
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {item.submenu.map((subItem, subIndex) => (
                  <ListItem
                    key={`${index}-${subIndex}`}
                    button
                    onClick={() => handleNavigation(subItem.link)}
                    sx={{
                      pl: 7,
                      pr: 2,
                      py: 1.2,
                      borderRadius: '8px',
                      mx: 1,
                      mb: 0.3,
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,128,0.05)',
                      },
                    }}
                  >
                    <ListItemText 
                      primary={subItem.text}
                      primaryTypographyProps={{
                        style: { 
                          fontSize: '14px',
                          color: '#555',
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  };

  // Don't render anything if screen size is greater than 990px
  if (!isMobile) {
    return null;
  }

  return (
    <div>
      <style>
        {`
          .mobile-header {
            background-color: #ffffff !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            z-index: 1100 !important;
          }

          .mobile-header .MuiToolbar-root {
            padding: 0 16px;
            min-height: 64px;
          }

          .mobile-logo {
            height: 40px;
            width: auto;
            display: block;
          }

          .drawer-content {
            flex: 1;
            overflow-y: auto;
            padding: 8px 0 80px 0;
          }

          .drawer-content::-webkit-scrollbar {
            width: 4px;
          }

          .drawer-content::-webkit-scrollbar-track {
            background: #f1f1f1;
          }

          .drawer-content::-webkit-scrollbar-thumb {
            background: #000080;
            border-radius: 10px;
          }

          .drawer-content::-webkit-scrollbar-thumb:hover {
            background: #0000a0;
          }

          .menu-icon-btn {
            color: #000000 !important;
            padding: 8px !important;
          }

          .menu-icon-btn:hover {
            background-color: rgba(0,0,0,0.05) !important;
          }
        `}
      </style>

      <AppBar position="fixed" className="mobile-header">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            className="menu-icon-btn"
          >
            <MenuIcon sx={{ fontSize: '28px' }} />
          </IconButton>
          
          <Link to="/" style={{ display: 'flex', alignItems: 'center', flex: 1, marginLeft: '8px' }}>
            <BrandLogo alt="ProTradesInsight" className="mobile-logo" />
          </Link>
        </Toolbar>
      </AppBar>

      <Drawer 
        anchor="left" 
        open={drawerOpen} 
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: 320,
            backgroundColor: '#ffffff',
            color: '#111827',
            boxShadow: '0 0 40px rgba(0,0,0,0.1)',
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            padding: '16px 20px',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#ffffff',
          }}
        >
          <Link to="/" onClick={() => setDrawerOpen(false)}>
            <BrandLogo alt="ProTradesInsight" style={{ height: '35px', width: 'auto' }} />
          </Link>
          <IconButton 
            onClick={toggleDrawer(false)} 
            sx={{ 
              color: '#000000',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Drawer Content - Navigation */}
        <Box
          className="drawer-content"
          sx={{ '& .MuiListItemIcon-root svg': { color: '#000080' } }}
        >
          <List sx={{ pt: 1 }}>
            {renderMenuItems(menuItems)}
          </List>

          <Divider sx={{ my: 2, mx: 2 }} />

          {/* Auth Section in Sidebar */}
          <Box sx={{ px: 2, py: 1 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                color: '#999', 
                fontWeight: 600, 
                fontSize: '12px',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                px: 1,
                mb: 1,
              }}
            >
              Account
            </Typography>
            
            {isAuthenticated ? (
              <>
                <ListItem
                  button
                  onClick={() => handleNavigation('/dashboard')}
                  sx={{
                    borderRadius: '8px',
                    mx: 0,
                    mb: 0.5,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,128,0.05)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <DashboardIcon sx={{ color: '#000080' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Dashboard"
                    primaryTypographyProps={{
                      style: { 
                        fontWeight: 500, 
                        fontSize: '15px',
                        color: '#1a1a1a',
                      },
                    }}
                  />
                </ListItem>
                
                <ListItem
                  button
                  onClick={handleLogout}
                  sx={{
                    borderRadius: '8px',
                    mx: 0,
                    mb: 0.5,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 0.08)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LogoutIcon sx={{ color: '#d32f2f' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Logout"
                    primaryTypographyProps={{
                      style: { 
                        fontWeight: 600, 
                        fontSize: '15px',
                        color: '#d32f2f',
                      },
                    }}
                  />
                </ListItem>
              </>
            ) : (
              <>
                <ListItem
                  button
                  onClick={() => handleNavigation('/login')}
                  sx={{
                    borderRadius: '8px',
                    mx: 0,
                    mb: 0.5,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,128,0.05)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LoginIcon sx={{ color: '#000080' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Log In"
                    primaryTypographyProps={{
                      style: { 
                        fontWeight: 500, 
                        fontSize: '15px',
                        color: '#1a1a1a',
                      },
                    }}
                  />
                </ListItem>
                
                <ListItem
                  button
                  onClick={() => handleNavigation('/signup')}
                  sx={{
                    borderRadius: '8px',
                    mx: 0,
                    mb: 0.5,
                    py: 1.5,
                    backgroundColor: 'rgba(0,0,128,0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,128,0.1)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <PersonAddIcon sx={{ color: '#000080' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Sign Up"
                    primaryTypographyProps={{
                      style: { 
                        fontWeight: 600, 
                        fontSize: '15px',
                        color: '#000080',
                      },
                    }}
                  />
                </ListItem>
              </>
            )}
          </Box>
        </Box>

        {/* Drawer Footer */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '16px 20px',
            borderTop: '1px solid #e9ecef',
            textAlign: 'center',
            backgroundColor: '#ffffff',
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#999',
              fontSize: '11px',
            }}
          >
            © 2024 ProTradesInsight. All rights reserved.
          </Typography>
        </Box>
      </Drawer>
    </div>
  );
};

export default MobileHeader;
